import { Dispatch, SetStateAction } from 'react';

import { SongPreview } from '~/interfaces';
import { genreKey, useGenreList } from '~/modules/songs/hooks/use-genre-list';
import { useLanguageList } from '~/modules/songs/hooks/use-language-list';
import {
  ANY_OPTION,
  CATEGORY_PICK_LABELS,
  DUETS_OPTIONS,
  FilterCategoryId,
  FilterOption,
  isLengthMatch,
  LENGTH_OPTIONS,
  LengthFilter,
  PLAYED_OPTIONS,
  PlayedFilter,
} from '~/routes/sing-a-song/song-selection/components/filters/filter-options';
import { AppliedFilters, filterValues } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

/**
 * What a filter category offers, what is on, and what picking an option does - shared by the TV's
 * filter panel and the phone's own filter sheet.
 *
 * Extracted rather than reimplemented because the phone has its own song list and the two have to
 * mean the same thing: a genre set on the phone and one set on the TV are the same question of the
 * same catalogue, and two copies of "picking a language reaches past the excluded-languages
 * setting" would drift on the first change to either.
 */

/** The TV's `PlaylistEntry` and the phone's `RemotePlaylistEntry` both reduce to these two. */
interface FilterPlaylist {
  name: string;
  label: string;
}

/**
 * The categories that hold more than one pick at a time. Genre, Language and Length are lists of
 * things a song simply is, and asking for two of them reads as "either" - there is no reason to
 * make someone choose between Pop and Rock. The other three are not: a song is a duet or it is
 * not, it has been played or it has not, and the playlist is the one view the screen is showing.
 */
const MULTI_CATEGORIES: FilterCategoryId[] = ['genre', 'language', 'length'];

export const isMultiCategory = (id: FilterCategoryId) => MULTI_CATEGORIES.includes(id);

/**
 * The fields the six categories actually read. The phone gets a trimmed copy of the song list -
 * only what fits comfortably over the wire - so it satisfies this without being a whole
 * `SongPreview`, the same way `searchList` takes what it can search.
 */
export type FilterableSong = Pick<SongPreview, 'id' | 'language'> &
  Partial<Pick<SongPreview, 'genre' | 'duration' | 'tracksCount'>>;

interface Options {
  /** Which categories this surface offers, in order. The phone leaves `playlist` out - see F15. */
  categories: FilterCategoryId[];
  /** Everything the surface can reach, which is what the option counts are measured against. */
  catalogue: FilterableSong[];
  /**
   * Where the language options come from. Deliberately separate from `catalogue`, which has already
   * had the excluded-languages setting applied: reading them from the filtered list would mean the
   * one thing this category replaced - reaching a language the setting hides - could never be done,
   * because the language would not be on the list to pick.
   */
  languageSource: Pick<SongPreview, 'language'>[];
  filters: AppliedFilters;
  setFilters: Dispatch<SetStateAction<AppliedFilters>>;
  playlists?: FilterPlaylist[];
  selectedPlaylist?: string | null;
  setSelectedPlaylist?: (name: string) => void;
}

export default function useFilterCategories({
  categories,
  catalogue,
  languageSource,
  filters,
  setFilters,
  playlists = [],
  selectedPlaylist = null,
  setSelectedPlaylist,
}: Options) {
  const genres = useGenreList(catalogue);
  const languages = useLanguageList(languageSource);

  const optionsFor = (id: FilterCategoryId): FilterOption[] => {
    switch (id) {
      case 'playlist':
        return playlists.map((playlist) => ({ value: playlist.name, label: playlist.label }));
      case 'genre':
        return [ANY_OPTION, ...genres.map(({ name, count }) => ({ value: name, label: name, count }))];
      case 'language':
        return [ANY_OPTION, ...languages.map(({ name, count }) => ({ value: name, label: name, count }))];
      case 'duets':
        return DUETS_OPTIONS;
      case 'played':
        return PLAYED_OPTIONS;
      case 'length':
        return LENGTH_OPTIONS.map((option) =>
          option.value
            ? { ...option, count: catalogue.filter((song) => isLengthMatch(song, option.value as LengthFilter)).length }
            : option,
        );
    }
  };

  /** Everything a category holds. Single-valued ones answer with none or one. */
  const selectedValues = (id: FilterCategoryId): string[] => {
    switch (id) {
      case 'playlist':
        return selectedPlaylist ? [selectedPlaylist] : [];
      case 'genre':
        return filterValues(filters.genre);
      case 'language':
        return filterValues(filters.language);
      case 'duets':
        return filters.duet === true ? ['duets'] : filters.duet === false ? ['solo'] : [];
      case 'played':
        return filters.played ? [filters.played] : [];
      case 'length':
        return filterValues(filters.length);
    }
  };

  /**
   * Genre is compared on the collapsed spelling, the way the counts are gathered, so a filter
   * arriving as "Kpop" still lights up the K-Pop option that holds all 101 of them.
   */
  const matchesPick = (id: FilterCategoryId, pick: string, value: string) =>
    id === 'genre' ? genreKey(pick) === genreKey(value) : pick === value;

  const isOptionSelected = (id: FilterCategoryId, value: string) => {
    const picks = selectedValues(id);
    // "Any" is not a pick of its own - it is what is left when a category holds nothing.
    if (value === '') return picks.length === 0;

    return picks.some((pick) => matchesPick(id, pick, value));
  };

  /** What picking an option leaves the category holding. `''` empties it; the rest toggle. */
  const nextValues = (id: FilterCategoryId, value: string): string[] => {
    if (value === '') return [];
    const picks = selectedValues(id);
    if (!isMultiCategory(id)) return [value];

    return isOptionSelected(id, value) ? picks.filter((pick) => !matchesPick(id, pick, value)) : [...picks, value];
  };

  const apply = (id: FilterCategoryId, value: string) => {
    if (id === 'playlist') return setSelectedPlaylist?.(value);

    const picked = nextValues(id, value);
    const [first] = picked;

    setFilters((current) => {
      switch (id) {
        case 'genre':
          return { ...current, genre: picked.length ? picked : undefined };
        case 'language':
          // Picking a language reaches past the excluded-languages setting, exactly as the language
          // playlists it replaces did - asking for Polish and being shown nothing reads as a bug.
          return {
            ...current,
            language: picked.length ? picked : undefined,
            skipExcludedLanguages: picked.length ? true : undefined,
          };
        case 'duets':
          return { ...current, duet: first === 'duets' ? true : first === 'solo' ? false : undefined };
        case 'played':
          return { ...current, played: first as PlayedFilter | undefined };
        case 'length':
          return { ...current, length: picked.length ? (picked as LengthFilter[]) : undefined };
        default:
          return current;
      }
    });
  };

  /**
   * What the category reads as when it is not open. Two picks are worth naming; past that the names
   * stop fitting the rail's line and the count is the more useful thing anyway.
   */
  const labelFor = (id: FilterCategoryId) => {
    if (id === 'playlist') {
      return playlists.find((entry) => entry.name === selectedValues(id)[0])?.label ?? 'All songs';
    }
    const picks = selectedValues(id);
    if (picks.length === 0) return ANY_OPTION.label;

    const options = optionsFor(id);
    const labels = picks.map(
      (pick) => options.find((option) => option.value && matchesPick(id, pick, option.value))?.label ?? pick,
    );

    return labels.length > 2 ? `${labels.length} ${CATEGORY_PICK_LABELS[id] ?? 'selected'}` : labels.join(', ');
  };

  /** Everything on right now, as a header lists it. The playlist is not one - it is always set. */
  const activeFilters = categories.filter((id) => id !== 'playlist' && selectedValues(id).length > 0);

  const clearAll = () => {
    setFilters((current) => ({
      ...current,
      genre: undefined,
      language: undefined,
      skipExcludedLanguages: undefined,
      duet: undefined,
      played: undefined,
      length: undefined,
    }));
  };

  return { optionsFor, selectedValues, isOptionSelected, apply, labelFor, activeFilters, clearAll, genres, languages };
}
